import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Sparkles, Play, Lock, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  fetchGeoOverview,
  fetchGeoRuns,
  triggerGeoRun,
} from "@/lib/api";
import ExplorePanel from "./ExplorePanel";
import MencionesPanel from "./MencionesPanel";
import BrandPerceptionPanel from "./BrandPerceptionPanel";
import EvolutionPanel from "./EvolutionPanel";
import PromptsManager from "./PromptsManager";
import EngineScoreCards from "./EngineScoreCards";
import RunSelector from "./RunSelector";

const POLL_INTERVAL = 4000;

export default function GeoIntelTab({ clientId, clientName }) {
  const qc = useQueryClient();
  const [activeRunId, setActiveRunId] = useState(null);
  const [activeTab, setActiveTab] = useState("explore");
  // Selected historical run for the Menciones + Brand tabs (defaults to latest)
  const [selectedRunId, setSelectedRunId] = useState(null);

  const overviewQ = useQuery({
    queryKey: ["geo-overview", clientId],
    queryFn: () => fetchGeoOverview(clientId),
    refetchInterval: activeRunId ? POLL_INTERVAL : false,
  });
  const runsQ = useQuery({
    queryKey: ["geo-runs", clientId],
    queryFn: () => fetchGeoRuns(clientId),
    refetchInterval: activeRunId ? POLL_INTERVAL : false,
  });

  // Auto-detect a queued/running run when component mounts.
  useEffect(() => {
    const running = (runsQ.data || []).find((r) =>
      ["queued", "running"].includes(r.status)
    );
    setActiveRunId(running?.id || null);
  }, [runsQ.data]);

  // Once active run completes, refresh + clear polling.
  useEffect(() => {
    if (!activeRunId) return;
    const run = (runsQ.data || []).find((r) => r.id === activeRunId);
    if (run && run.status === "completed") {
      setActiveRunId(null);
      setSelectedRunId(run.id); // auto-switch to the fresh run
      qc.invalidateQueries({ queryKey: ["geo-overview", clientId] });
      qc.invalidateQueries({ queryKey: ["geo-evolution", clientId] });
      qc.invalidateQueries({ queryKey: ["geo-snapshot", clientId] });
      qc.invalidateQueries({ queryKey: ["geo-run", clientId, activeRunId] });
      toast.success("Análisis completado");
    } else if (run && run.status === "failed") {
      setActiveRunId(null);
      toast.error(`Análisis falló: ${run.error || "error desconocido"}`);
    }
  }, [activeRunId, runsQ.data, clientId, qc]);

  // Initialize selectedRunId to the latest completed run when data arrives
  useEffect(() => {
    if (selectedRunId) return;
    const completed = (runsQ.data || []).filter((r) => r.status === "completed");
    if (completed.length) setSelectedRunId(completed[0].id);
  }, [runsQ.data, selectedRunId]);

  const runMut = useMutation({
    mutationFn: () => triggerGeoRun(clientId),
    onSuccess: (res) => {
      setActiveRunId(res.run_id);
      toast.success(
        `Análisis iniciado — ${res.prompts_count} prompts × ${res.engines.length} motores`
      );
      qc.invalidateQueries({ queryKey: ["geo-runs", clientId] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || "No se pudo iniciar el análisis");
    },
  });

  const overview = overviewQ.data;
  const activeRun = (runsQ.data || []).find((r) => r.id === activeRunId);
  const completedRuns = (runsQ.data || []).filter((r) => r.status === "completed");
  const effectiveRunId = selectedRunId || overview?.latest_run?.id;

  return (
    <div className="space-y-6" data-testid="geo-intel-tab">
      {/* SUB-TABS — Explore first */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="h-auto p-1 bg-slate-100 border border-slate-200 rounded-md flex flex-wrap gap-1">
          <TabsTrigger
            value="explore"
            data-testid="geo-subtab-explore"
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-violet-700 text-slate-600 px-3.5 py-1.5 rounded-sm text-sm font-medium gap-1.5"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Explore
          </TabsTrigger>
          <TabsTrigger
            value="menciones"
            data-testid="geo-subtab-menciones"
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3.5 py-1.5 rounded-sm text-sm font-medium"
          >
            Menciones · Categoría
          </TabsTrigger>
          <TabsTrigger
            value="brand"
            data-testid="geo-subtab-brand"
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3.5 py-1.5 rounded-sm text-sm font-medium"
          >
            Cómo me ve la IA
          </TabsTrigger>
          <TabsTrigger
            value="evolution"
            data-testid="geo-subtab-evolution"
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3.5 py-1.5 rounded-sm text-sm font-medium"
          >
            Evolución
          </TabsTrigger>
          <TabsTrigger
            value="prompts"
            data-testid="geo-subtab-prompts"
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3.5 py-1.5 rounded-sm text-sm font-medium"
          >
            Prompts y configuración
          </TabsTrigger>
        </TabsList>

        <TabsContent value="explore" className="mt-6">
          <ExplorePanel clientId={clientId} clientName={clientName} />
        </TabsContent>

        {/* Dashboard header (KPIs + score cards + Ejecutar análisis) only when
            user is NOT on Explore — keeps the lab tab clean and focused. */}
        {activeTab !== "explore" && (
          <DashboardHeader
            clientName={clientName}
            overview={overview}
            activeRunId={activeRunId}
            activeRun={activeRun}
            onRun={() => runMut.mutate()}
            runPending={runMut.isPending}
          />
        )}

        <TabsContent value="menciones" className="mt-6 space-y-4">
          {completedRuns.length > 0 && (
            <RunSelector
              clientId={clientId}
              runs={completedRuns}
              selectedRunId={effectiveRunId}
              onChange={setSelectedRunId}
            />
          )}
          <MencionesPanel
            clientId={clientId}
            runId={effectiveRunId}
          />
        </TabsContent>
        <TabsContent value="brand" className="mt-6 space-y-4">
          {completedRuns.length > 0 && (
            <RunSelector
              clientId={clientId}
              runs={completedRuns}
              selectedRunId={effectiveRunId}
              onChange={setSelectedRunId}
            />
          )}
          <BrandPerceptionPanel
            clientId={clientId}
            runId={effectiveRunId}
            snapshotRunId={overview?.snapshot_run?.id}
          />
        </TabsContent>
        <TabsContent value="evolution" className="mt-6">
          <EvolutionPanel clientId={clientId} />
        </TabsContent>
        <TabsContent value="prompts" className="mt-6">
          <PromptsManager clientId={clientId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─────────────── dashboard header (KPIs + run button + score cards) ───────────────

function DashboardHeader({ clientName, overview, activeRunId, activeRun, onRun, runPending }) {
  return (
    <div className="mt-6 space-y-6">
      <div className="rounded-md border border-slate-200 bg-gradient-to-br from-violet-50 via-white to-white p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-10 w-10 rounded-md bg-violet-600 text-white flex items-center justify-center shrink-0">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h2 className="font-display text-2xl font-semibold text-slate-900">
                Visibilidad IA
              </h2>
              <p className="text-sm text-slate-600 mt-0.5">
                Cómo las IA mencionan a <span className="font-medium">{clientName}</span> y a sus competidores. Tracking sobre ChatGPT · Claude · Gemini · Perplexity.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <RunStatusBadge activeRun={activeRun} />
            <Button
              onClick={onRun}
              disabled={!!activeRunId || runPending}
              className="bg-violet-600 hover:bg-violet-700 text-white gap-2"
              data-testid="geo-run-button"
            >
              {activeRunId ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Analizando…
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Ejecutar análisis
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <KPI label="Total runs" value={overview?.total_runs ?? "—"} testid="geo-kpi-runs" />
          <KPI
            label="Snapshot Día 0"
            value={
              overview?.snapshot_run ? (
                <span className="inline-flex items-center gap-1 text-emerald-700">
                  <Lock className="h-3 w-3" /> Activo
                </span>
              ) : (
                "Sin definir"
              )
            }
            testid="geo-kpi-snapshot"
          />
          <KPI
            label="Última run"
            value={
              overview?.latest_run
                ? new Date(overview.latest_run.completed_at || overview.latest_run.started_at).toLocaleDateString("es-AR")
                : "—"
            }
            testid="geo-kpi-latest"
          />
          <KPI
            label="Prompts en última run"
            value={overview?.latest_run?.prompts_count || "—"}
            testid="geo-kpi-prompts"
          />
        </div>
      </div>

      <EngineScoreCards scoreByEngine={overview?.score_by_engine} />
    </div>
  );
}

// ─────────────── helpers ───────────────

function KPI({ label, value, testid }) {
  return (
    <div className="rounded-md bg-white border border-slate-200 p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
        {label}
      </div>
      <div className="font-display text-xl font-semibold text-slate-900 mt-1">
        {value}
      </div>
    </div>
  );
}

function RunStatusBadge({ activeRun }) {
  if (!activeRun) return null;
  const tone =
    activeRun.status === "running"
      ? "bg-violet-100 text-violet-700"
      : "bg-amber-100 text-amber-700";
  return (
    <span
      className={`text-xs px-2 py-1 rounded-sm inline-flex items-center gap-1 ${tone}`}
      data-testid="geo-run-status-badge"
    >
      <AlertCircle className="h-3 w-3" />
      {activeRun.status === "running" ? "Ejecutando…" : "En cola"}
    </span>
  );
}
